import { useCallback, useState } from "react";

import type { CatalogListItem } from "@/lib/catalog-item";
import { ownItemSelection, resolveVariantSelection } from "@/lib/catalog-item-variants";
import { useCatalogItemDetail } from "./catalog-items";

// The catalog card and list row's variant preview: which sibling the view is
// showing, and the lazy read that fills it in.
//
// **One hook where the donor has two identical copies.** `item-card.tsx:77-89`
// and `item-data-row.tsx:70-82` are the same eight lines twice — the two views
// draw different markup around the *same* four values, so the duplication is the
// donor's accident rather than its design.
//
// The lazy read is the load-bearing part. A card grid is twenty of these on
// screen, and the list payload already carries everything a card needs for its
// *own* item; only a sibling needs fetching, because a group member arrives as
// `{ itemId, heroImage }` and a card shows a name, a category, a model number and
// a finish. So the query is disabled until someone picks a sibling — the donor's
// `{ skip: isOwnItem }` — and while it is in flight the card keeps drawing its own
// item rather than blanking.

export interface CatalogVariantPreview {
  /**
   * The item the view should draw — the picked sibling once it has landed, the
   * card's own item until then.
   *
   * Nearly everything on the card follows this: the badges, the picture, the EI
   * mark and chips, the manufacturer, the name *and its link*, and all four
   * fields. What does **not** is the card's own controls — the selection checkbox
   * and the actions menu, which the donor deliberately drives from the own-item
   * flags: "the admin actions menu ... always operates on this card's item rather
   * than the variant currently being previewed" (`item-card.tsx:52-54`). That is
   * the opposite of the details page, where the action menu follows the variant.
   */
  displayItem: CatalogListItem;
  /** Which tile is pressed. */
  selectedVariantId: number;
  /** Which tile is spinning, if any. */
  loadingVariantId: number | undefined;
  /**
   * The previewed sibling's read failed, and is not being retried.
   *
   * The catalog card says nothing about it — its donor has no error arm and keeps
   * drawing the own item. The Item Search island's card raises its donor's
   * `ITEM_VARIANTS` snackbar on it (`ItemCard.tsx`, `isErrorVariantsData`, #798).
   */
  failed: boolean;
  onSelect: (variantId: number) => void;
}

export function useCatalogVariantPreview(item: CatalogListItem): CatalogVariantPreview {
  // Bound to the item it was made against, so nothing has to reset it when a
  // virtualised or reordered list hands this instance a different item. The guard
  // itself is `resolveVariantSelection`, under unit test in
  // `lib/catalog-item-variants.ts`.
  const [selection, setSelection] = useState(() => ownItemSelection(item.id));
  const selectedVariantId = resolveVariantSelection(selection, item.id);
  const isOwnItem = selectedVariantId === item.id;

  const variant = useCatalogItemDetail(selectedVariantId, { enabled: !isOwnItem });

  const onSelect = useCallback(
    (variantId: number) => setSelection({ baseId: item.id, variantId }),
    [item.id],
  );

  return {
    displayItem: !isOwnItem && variant.data ? variant.data.item : item,
    selectedVariantId,
    // `!isOwnItem && variantQuery.isFetching` — a *refetch* of the previewed
    // sibling spins its tile too, which is the donor's behaviour and reads
    // correctly: the tile is the only thing that says a read is happening.
    loadingVariantId: !isOwnItem && variant.isFetching ? selectedVariantId : undefined,
    failed: !isOwnItem && variant.isError && !variant.isFetching,
    onSelect,
  };
}
